import { Inject } from '@odinjs/odin';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnBlur, OnChange, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

import { applicationContext, foundationContext, ParameterValue } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';

import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import RouteUtils, { RouteError } from '../../../../utils/route-utils';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { SimpleDialog } from '../../../dialog-helper';
import { InfusionsViewerParams } from '../../infusion-viewer/infusions-viewer-params';
import { GenerateChangePrescribedSolutionParams, ManagementSolution } from '../../services/solution-service';
import SolutionService from '../../services/solution-service';
import { InfusionInterruptionBaseController } from '../infusion-interrupt-base-controller';

import './infusion-interrupt-solution-controller.css';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID } from '@philips-internal/oswald-ui-utils';

interface ReasonInterruptSolutionType {
  emarInterruptReasonSequence: number;
  eventStatus: string;
  isBottleExchdange: boolean;
  observationStatus: string;
  reasonDescription: string;
  requiresVolumeStatus: string;
}

interface SolutionDetail {
  date: string;
  emarVolumeQuantity: number;
  infusedVolumeQuantity: number;
  orderValidityDate?: moment.Moment;
  partialVolumeQuantity: number;
  partialVolumeQuantityBI: number;
  solutionDescription: string;
  unitMeasureCode: string;
}

interface ReasonInterruptSolutionFormData {
  discardedVolumeQuantity: number;
  infusedVolumeQuantity: number;
  partialVolumeQuantity: number;
  reasonInterruptDescription: string;
  reasonInterruptType: ReasonInterruptSolutionType;
}

@Controller({ fragment: 'infusion-interrupt-solution' })
class InfusionStopSolution extends InfusionInterruptionBaseController {

  @Inject('form-solution')
  formSolutionAPI!: FormAPI;

  @Inject('interrupt-solution-form')
  interruptSolutionFormAPI!: FormAPI;

  @Inject('infused-volume-unit')
  infusedUnitTextAPI!: TextAPI;

  @Inject('discarded-volume-unit')
  dicardedUnitTextAPI!: TextAPI;

  @Inject('infused-volume-field')
  infusedVolumeFieldAPI!: FieldAPI;

  @Inject('discarded-volume-field')
  discardedVolumeFieldAPI!: FieldAPI;

  @Inject('infused-volume-input')
  infusedVolumeInputAPI!: InputAPI;

  @Inject('discarded-volume-input')
  discardedVolumeInputAPI!: InputAPI;

  solutionService = new SolutionService();

  private async getManagementSolution(): Promise<void> {
    const prescriptionNumber = this.context.get('prescriptionNumber') as number;
    const solutionSequence = InfusionsViewerParams.adminSchedule.solutionSequenceNumber as number;

    const routeSafeGetManagementSolution = RouteUtils.routeSafeAsync(this.solutionService.getManagementSolution);

    try {
      const managementSolution = await routeSafeGetManagementSolution(
        prescriptionNumber,
        solutionSequence
      );

      const isoCurrentDate = moment().format(ISO_FULL_DATE_FORMAT);
      this.context.set({isoCurrentDate});
      this.context.set({managementSolution});

      const solutionDetail = {
        date: isoCurrentDate,
        emarVolumeQuantity: managementSolution.emarVolumeQuantity,
        infusedVolumeQuantity: managementSolution.infusedVolumeQuantity,
        orderValidityDate: managementSolution.orderValidityDate,
        partialVolumeQuantity: managementSolution.partialVolumeQuantity,
        partialVolumeQuantityBI: managementSolution.partialVolumeQuantityBI,
        solutionDescription: managementSolution.solutionDescription,
        unitMeasureCode: managementSolution.unitMeasureCode,
      }

      this.context.set({solutionDetail});

      this.fillSolutionDetailForm();

      this.verifyParam565();

      this.infusedUnitTextAPI.setValue(MessageService.translate('tws.measure-label'));
      this.dicardedUnitTextAPI.setValue(MessageService.translate('tws.measure-label'));
    } catch(error) {
      const isRouteError = error instanceof RouteError
      if(!isRouteError) {
        SimpleDialog.showErrorMessage(error as string);
      }
    }
  }

  private verifyParam565(): void {
    const automaticallycalculaterInfusedParam = this.systemParameterReader('ADEP#1113', 'AUTOMATICALLY_CALCULATE_INFUSED_VOLUME_SOLUTIONS#565');
    const { emarVolumeQuantity, partialVolumeQuantityBI, infusedVolumeQuantity } = this.context.get('solutionDetail') as SolutionDetail;
    if (automaticallycalculaterInfusedParam === 'S') {
        this.infusedVolumeFieldAPI.setValue(String(infusedVolumeQuantity));
        const newValue = Number(emarVolumeQuantity) - Number(partialVolumeQuantityBI) - Number(infusedVolumeQuantity);
        this.discardedVolumeFieldAPI.setValue(String(newValue));
    } else {
        this.infusedVolumeFieldAPI.setValue(String(emarVolumeQuantity));
        this.discardedVolumeFieldAPI.setValue('0');
    }
  }

  private fillSolutionDetailForm(): void {
    const solutionDetail = this.context.get('solutionDetail') as SolutionDetail;
    const measureLabel = MessageService.translate('tws.measure-label');

    const solutionDetailView = {
      date: solutionDetail.date,
      emarVolumeQuantity: `${solutionDetail.emarVolumeQuantity || '0'} ${measureLabel}`,
      partialVolumeQuantity: `${solutionDetail.partialVolumeQuantity || '0'} ${measureLabel}`,
      partialVolumeQuantityBI: `${solutionDetail.partialVolumeQuantityBI || '0'} ${measureLabel}`,
      solutionDescription: solutionDetail.solutionDescription,
      unitMeasureCode: solutionDetail.unitMeasureCode,
    }

    this.formSolutionAPI.setData(solutionDetailView as Record<string, Value>);
  }

  getFieldValue(fieldName: string): Value {
    const interruptSolutionForm = this.interruptSolutionFormAPI;
    const record = interruptSolutionForm.getRecord();

    return record[fieldName];
  }

  systemParameterReader(functionCode: string, parameterCode: string): ParameterValue | undefined {
    if (foundationContext.isEmpty()) {
      return undefined;
    }

    const modules = foundationContext.getAvailableModules();
    const module = modules?.find(item => item.id == functionCode);

    return module?.parameters.get(parameterCode);
  }

  validateReasonInterruptSolutionSelected(): boolean {
    const reasonInterruptType = this.getFieldValue('reasonInterruptType') as ReasonInterruptSolutionType;

    if (!reasonInterruptType) {
      return false;
    }

    const solutionDetail = this.context.get('solutionDetail') as SolutionDetail;

    const infusedVolumeQuantity = Number(this.getFieldValue('infusedVolumeQuantity'));
    const emarVolumeQuantity = Number(solutionDetail.emarVolumeQuantity);
    const discardedVolumeQuantity = Number(this.getFieldValue('discardedVolumeQuantity'));
    const automaticallycalculaterInfusedParam = this.systemParameterReader('ADEP#1113', 'AUTOMATICALLY_CALCULATE_INFUSED_VOLUME_SOLUTIONS#565');

    const requiredMessage = MessageService.translate('tws.emar.required-specification-volumes').replace('#@ds_motivo#@', reasonInterruptType.reasonDescription);
    const notAllowsMessage = MessageService.translate('tws.emar.not-allow-specification-volume').replace('#@ds_motivo#@', reasonInterruptType.reasonDescription);

    if (reasonInterruptType.requiresVolumeStatus !== 'N') {

      if (automaticallycalculaterInfusedParam === 'S') {

        if (!infusedVolumeQuantity && !discardedVolumeQuantity) {
          this.showToastMessage(requiredMessage);
          return false;
        }

      } else {

        if (!emarVolumeQuantity && !discardedVolumeQuantity) {
          this.showToastMessage(requiredMessage);
          return false;
        }

      }

    } else {
      if (automaticallycalculaterInfusedParam === 'S') {

        if (infusedVolumeQuantity && discardedVolumeQuantity) {
          this.showToastMessage(notAllowsMessage);
          return false;
        }

      } else {

        if (emarVolumeQuantity && discardedVolumeQuantity) {
          this.showToastMessage(notAllowsMessage);
          return false;
        }

      }

    }

    return true;
  }

  showToastMessage(message: string): void {
    ToastService.open({
      dismissExpression: MessageService.translate('tws-ok.ok-text'),
      dismissible: true,
      highlight: false,
      id: generateUUID(),
      level: ToastLevel.WARNING,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  showToastSuccess(): void {
    ToastService.open({
      dismissible: true,
      id: generateUUID(),
      level: ToastLevel.SUCCESS,
      message: MessageService.translate('tws.emar.interrupt-solution-success'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
    });
  }

  validateObservationDescription(): boolean {
    const reasonInterruptType = this.getFieldValue('reasonInterruptType') as ReasonInterruptSolutionType;

    if (!reasonInterruptType) {
      return false;
    }

    const reasonInterruptDescription = this.getFieldValue('reasonInterruptDescription') as string;
    const requiredMessage = MessageService.translate('tws.emar.observation-required').replace('#@ds_motivo#@', reasonInterruptType.reasonDescription);

    if (reasonInterruptType.observationStatus === 'P' && (!reasonInterruptDescription || reasonInterruptDescription.length === 0)) {
      this.showToastMessage(requiredMessage);
      return false;
    }

    return true;
  }

  validateVolumeQuantity(): boolean {
    const managementSolution = this.context.get('managementSolution') as ManagementSolution;

    const infusedVolumeQuantity = this.getFieldValue('infusedVolumeQuantity') as number;
    const partialVolumeQuantity = this.getFieldValue('partialVolumeQuantity') ? this.getFieldValue('partialVolumeQuantity') : 0 as number;
    const volumeQuantity = Number(infusedVolumeQuantity) + Number(partialVolumeQuantity);
    const emarVolumeQuantity = Number(managementSolution.emarVolumeQuantity);
    if (volumeQuantity > emarVolumeQuantity) {
      this.showToastMessage(MessageService.translate('tws.emar.sum-partial-volume-greater-bag-volume'));
      return false;
    }

    return true;
  }

  validateVialChange(): boolean {
    const allowVialChange = this.systemParameterReader('ADEP#1113', 'ALLOWS_YOU_START_OR_VIAL_CHANGE_AFTER_VALIDATING_PRESCRIPTION#23');
    const solutionDetail = this.context.get('solutionDetail') as SolutionDetail;
    const orderValidityDate = solutionDetail.orderValidityDate as moment.Moment;

    if (allowVialChange !== 'S' && orderValidityDate && moment().isAfter(orderValidityDate)) {
      this.showToastMessage(MessageService.translate('tws.emar.not-permission-start-solution-vial-change'));
      return false;
    }

    return true;
  }

  validateInfusedVolumeGreaterSolution(): boolean {
    const managementSolution = this.context.get('managementSolution') as ManagementSolution;

    const allowInfuseVolumeGreater = this.systemParameterReader('ADEP#1113', 'ALLOWS_INFUSE_VOLUME_GREATER_TOTAL_VOLUME_PRESCRIBED_SOLUTION#24');
    const isPrdPrn = managementSolution.isPdo || managementSolution.isPrn;
    const infusedVolumeQuantity = this.getFieldValue('infusedVolumeQuantity') as number;
    const totalSolutionQuantity = Number(managementSolution.totalSolutionQuantity);
    const currentInfusionVolumeTotal = Number(managementSolution.currentInfusionVolumeTotal);

    if (allowInfuseVolumeGreater !== 'S' && isPrdPrn && totalSolutionQuantity > 0 && infusedVolumeQuantity > 0) {
      const totalVolume = currentInfusionVolumeTotal + infusedVolumeQuantity;

      if (totalVolume > totalSolutionQuantity) {
        this.showToastMessage(MessageService.translate('tws.emar.not-authorized-infuse-volume-greater-solution'));
        return false;
      }
    }

    return true;
  }

  validate(): boolean {
    return this.validateReasonInterruptSolutionSelected() &&
      this.validateObservationDescription() &&
      this.validateVolumeQuantity() &&
      this.validateVialChange() &&
      this.validateInfusedVolumeGreaterSolution();
  }

  private generateChangePrescribedSolution(): void {
    const interruptSolutionForm = this.interruptSolutionFormAPI;
    const record = interruptSolutionForm.getRecord();
    const formData = record as Record<string, ReasonInterruptSolutionFormData>;
    const reasonInterruptType = formData.reasonInterruptType as unknown as ReasonInterruptSolutionType;

    const encounterNumber = this.context.get('encounterNumber') as number;
    const prescriptionNumber = this.context.get('prescriptionNumber') as number;
    const solutionSequence = this.context.get('solutionSequence') as number;
    const isoCurrentDate = this.context.get('isoCurrentDate') as string;
    const managementSolution = this.context.get('managementSolution') as ManagementSolution;

    const solutionDetail = this.context.get('solutionDetail') as SolutionDetail;

    const params: GenerateChangePrescribedSolutionParams = {
      changeDate: isoCurrentDate,
      discardedVolumeQuantity: Number(formData.discardedVolumeQuantity),
      dosageType: String(managementSolution.dosageType),
      encounterNumber: encounterNumber,
      infusedVolumeQuantity: Number(formData.infusedVolumeQuantity),
      infusionSpeedQuantity: Number(managementSolution.currentInfusionVolumeTotal),
      observationDescription: String(formData.reasonInterruptDescription),
      partialVolumeQuantity: Number(formData.partialVolumeQuantity),
      prescriptionNumber: prescriptionNumber,
      reasonSequence: reasonInterruptType.emarInterruptReasonSequence,
      solutionSequence: solutionSequence,
      volumePhaseQuantity: solutionDetail.emarVolumeQuantity
    }

    const {
      encounterId,
      patientId,
    } = this.historyService.getCurrentRouteParams();

    this.solutionService.GenerateChangePrescribedSolution(params)
      .then(response => {
        if (response && response.messageDescription) {
          this.showToastMessage(response.messageDescription)
        } else {
          this.showToastSuccess();
        }
      }).catch(err => {
        if (err && err.messageDescription) {
          this.showToastMessage(err.messageDescription)
        }
      }).finally(() => {
        applicationContext.clearNavigationStack();
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
          encounterId,
          patientId,
        });
      });
  }

  @OnMount({ target: 'interrupt-solution-form' })
  infusionStopSolutionLayoutMount(): void {
    InformationBarUtils.collapsedBar = true;
    InfusionInterruptionBaseController.routeInStack.infusionInterruptSolution = true;

    const {
      patientId,
      encounterId,
      prescriptionNumber,
      sequenceNumber
    } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      encounterNumber: encounterId,
      entryChangeEnabled: true,
      patientCode: patientId,
      patientId,
      prescriptionNumber: prescriptionNumber,
      solutionSequence: sequenceNumber,
    });

    this.getManagementSolution();
  }

  @OnMount('reason-interrupt-fetch')
  onFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const eventStatus = 'IS';

    fetchReason.setParameters({ eventStatus }, { skip: true });
  }

  @OnUnmount({ target: 'interrupt-solution-layout' })
  infusionStopSolutionLayoutUnmount(): void {
    InfusionInterruptionBaseController.routeInStack.infusionInterruptSolution = false;
  }

  @OnBeforeCancel({ target: 'interrupt-solution-form' })
  cancelInterruptSolutionButtonClick(): void {
    applicationContext.popNavigationItem();

    if(InfusionInterruptionBaseController.routeInStack.infusionPartialVolume){
      applicationContext.popNavigationItem();
    }
  }

  @OnBeforeSubmit({ target: 'interrupt-solution-form' })
  async onSaveButtonClick(): Promise<void> {
    const isValid = await this.interruptSolutionFormAPI.validate();

    if (isValid && this.validate()) {
      this.generateChangePrescribedSolution();
    }
  }

  @OnBlur({ target: 'infused-volume-field' })
  onInfusedVolumeFieldBlur(): void {
    const infusedVolume = this.infusedVolumeFieldAPI.getValue();

    if(String(infusedVolume) !== '0' && !infusedVolume){
      return;
    }

    const {emarVolumeQuantity, partialVolumeQuantityBI} = this.context.get('solutionDetail') as SolutionDetail;

    const newValue = Number(emarVolumeQuantity) - Number(partialVolumeQuantityBI) - Number(infusedVolume);

    this.discardedVolumeFieldAPI.setValue(String(newValue));
  }

  @OnBlur({ target: 'discarded-volume-field' })
  onDiscardedVolumeFieldBlur(): void {
    const discardedVolume = this.discardedVolumeFieldAPI.getValue();

    if(String(discardedVolume) !== '0' && !discardedVolume){
      return;
    }

    const {emarVolumeQuantity, partialVolumeQuantityBI} = this.context.get('solutionDetail') as SolutionDetail;

    const newValue = Number(emarVolumeQuantity) - Number(partialVolumeQuantityBI) - Number(discardedVolume);

    this.infusedVolumeFieldAPI.setValue(String(newValue));
  }

  @OnChange({ target: 'reason-interrupt-field' })
  onReasonChange(evt: ChangeEvent<FieldAPI>): void {
    const reason = evt.value as unknown as ReasonInterruptSolutionType;
    const isInfusionPerSchedule = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.VIEW_SOLUTION_RELATION_TAB);
    const entryChangeEnabled = this.context.get('entryChangeEnabled');

    if(reason.requiresVolumeStatus === 'N') {
      this.disableEntryChange(this.infusedVolumeFieldAPI, this.infusedVolumeInputAPI);
      this.disableEntryChange(this.discardedVolumeFieldAPI, this.discardedVolumeInputAPI);
      this.context.set({entryChangeEnabled: false});
    } else if(!entryChangeEnabled) {
      this.enableEntryChange(this.infusedVolumeFieldAPI, this.infusedVolumeInputAPI);
      this.enableEntryChange(this.discardedVolumeFieldAPI, this.discardedVolumeInputAPI);
      this.context.set({entryChangeEnabled: true});
    }

    if (isInfusionPerSchedule === 'H') {
        const record = this.interruptSolutionFormAPI.getRecord();
        record.reasonInterruptDescription = reason.reasonDescription;
        record.reasonInterruptType = reason;
        this.interruptSolutionFormAPI.setData(record);
    }
  }

  enableEntryChange(fieldApi: FieldAPI, inputApi: InputAPI): void {
    fieldApi.setValue('');
    fieldApi.validate();
    inputApi.setEnabled(true);
  }

  disableEntryChange(fieldApi: FieldAPI, inputApi: InputAPI): void {
    fieldApi.setValue('0');
    fieldApi.validate();
    inputApi.setEnabled(false);
  }

}

export default InfusionStopSolution;